import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BEHAVIOR_SIGNALS } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Radio, RefreshCw, Save, Info, AlertTriangle, CheckCircle2,
  CircleOff, MinusCircle,
} from "lucide-react";
import HintIcon from "@/components/HintIcon";

const STATUS_META = {
  receiving:                { tone: "emerald", icon: CheckCircle2,   label: "Receiving" },
  receiving_with_rejections:{ tone: "amber",   icon: AlertTriangle,  label: "Receiving · Some Rejections" },
  no_recent_data:           { tone: "amber",   icon: AlertTriangle,  label: "No Recent Data" },
  no_recent_data_with_rejections: { tone: "red", icon: AlertTriangle, label: "No Recent Data · Rejections" },
  never_received:           { tone: "slate",   icon: CircleOff,      label: "Never Received" },
  not_active_yet:           { tone: "slate",   icon: MinusCircle,    label: "Not Active Yet" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { tone: "slate", icon: Info, label: status };
  const Icon = meta.icon;
  const t = meta.tone;
  const cls = {
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-300",
    amber:   "bg-amber-100 text-amber-700 border-amber-300",
    red:     "bg-red-100 text-red-700 border-red-300",
    slate:   "bg-slate-100 text-slate-600 border-slate-300",
  }[t];
  return (
    <span
      data-testid={BEHAVIOR_SIGNALS.operationalBadge}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}
    >
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

function Toggle({ checked, onChange, disabled, testId, label, hint }) {
  return (
    <div className="flex items-center gap-3 select-none">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        data-testid={testId}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 border shrink-0 ${
          checked ? "bg-indigo-600 border-indigo-700" : "bg-slate-200 border-slate-300"
        } disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300`}
      >
        <span
          className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
      <div className="flex items-center gap-1.5">
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        {hint && <HintIcon hint={hint} />}
      </div>
    </div>
  );
}

function SignalCard({ sig, onUpdate }) {
  const [tracking, setTracking] = useState(sig.tracking_supported);
  const [enabled, setEnabled] = useState(sig.enabled);
  const [minA, setMinA] = useState(sig.min_app_version_android || "");
  const [minI, setMinI] = useState(sig.min_app_version_ios || "");
  const [busy, setBusy] = useState(false);
  const dirty =
    tracking !== sig.tracking_supported ||
    enabled !== sig.enabled ||
    (minA || "") !== (sig.min_app_version_android || "") ||
    (minI || "") !== (sig.min_app_version_ios || "");

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        tracking_supported: tracking,
        enabled: enabled,
        min_app_version_android: minA || null,
        min_app_version_ios: minI || null,
      };
      // Preserve platforms — if tracking on but empty platforms, default both
      if (tracking && (!sig.supported_platforms || sig.supported_platforms.length === 0)) {
        patch.supported_platforms = ["android", "ios"];
      }
      await api.patch(`/behavior-signals/${sig.key}`, patch);
      toast.success(`Saved · ${sig.label}`);
      onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid={BEHAVIOR_SIGNALS.signalCard}
      className="border border-slate-200 rounded-lg bg-white p-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-slate-900">{sig.label}</div>
          <div className="text-[10px] font-mono text-slate-500">{sig.key} · {sig.category}</div>
          {sig.description && <div className="text-xs text-slate-600 mt-1 max-w-md">{sig.description}</div>}
        </div>
        <StatusPill status={sig.operational_status} />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3 text-center">
        <div className="rounded border border-slate-200 px-2 py-1.5">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500 flex items-center justify-center gap-1">
            Total
            <HintIcon hint="Lifetime count of accepted events for this signal — since the signal was first activated. Rejections are counted separately below." />
          </div>
          <div className="tabular font-bold text-slate-800" data-testid={BEHAVIOR_SIGNALS.totalEvents}>
            {sig.total_accepted_events?.toLocaleString?.() ?? sig.total_accepted_events}
          </div>
        </div>
        <div className="rounded border border-slate-200 px-2 py-1.5">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500 flex items-center justify-center gap-1">
            Today
            <HintIcon hint="Accepted events received in the last 24 hours. Watch for sudden drops — indicates a broken client or a rolled-back app version." />
          </div>
          <div className="tabular font-bold text-slate-800" data-testid={BEHAVIOR_SIGNALS.eventsToday}>
            {sig.events_last_24h}
          </div>
        </div>
        <div className="rounded border border-slate-200 px-2 py-1.5">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500 flex items-center justify-center gap-1">
            7 days
            <HintIcon hint="Accepted events in the last 7 days. A gentler smoother — useful for confirming a steady stream is coming through." />
          </div>
          <div className="tabular font-bold text-slate-800" data-testid={BEHAVIOR_SIGNALS.events7d}>
            {sig.events_last_7d}
          </div>
        </div>
        <div className="rounded border border-slate-200 px-2 py-1.5">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500 flex items-center justify-center gap-1">
            Users
            <HintIcon hint="Distinct trial users that produced at least one event of this type. If this stays at 1-2 while Total grows, one user is spamming — investigate." />
          </div>
          <div className="tabular font-bold text-slate-800" data-testid={BEHAVIOR_SIGNALS.uniqueUsers}>
            {sig.unique_users}
          </div>
        </div>
        <div className="rounded border border-slate-200 px-2 py-1.5">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-slate-500 flex items-center justify-center gap-1">
            Rejected
            <HintIcon hint="Events that failed validation and were NOT stored. Common causes: bad payload schema, unknown user_id, event too old (>24h). If this is climbing, the mobile client is sending malformed data." />
          </div>
          <div className={`tabular font-bold ${sig.rejected_count > 0 ? "text-red-700" : "text-slate-800"}`}
               data-testid={BEHAVIOR_SIGNALS.rejectedCount}>
            {sig.rejected_count}
          </div>
        </div>
      </div>

      {/* Observed rollout coverage */}
      {(sig.platforms_observed?.length > 0 || sig.app_versions_observed?.length > 0) && (
        <div className="mt-3 text-[11px] flex flex-wrap gap-x-4 gap-y-1">
          <div>
            <span className="text-slate-500 font-semibold">Platforms:</span>{" "}
            <span data-testid={BEHAVIOR_SIGNALS.platforms} className="font-mono text-slate-800">
              {sig.platforms_observed?.length ? sig.platforms_observed.join(", ") : "—"}
            </span>
          </div>
          <div>
            <span className="text-slate-500 font-semibold">Versions:</span>{" "}
            <span data-testid={BEHAVIOR_SIGNALS.versions} className="font-mono text-slate-800">
              {sig.app_versions_observed?.length ? sig.app_versions_observed.join(", ") : "—"}
            </span>
          </div>
          {sig.last_received_at && (
            <div>
              <span className="text-slate-500 font-semibold">Last:</span>{" "}
              <span className="font-mono text-slate-800">{new Date(sig.last_received_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      {/* Config toggles */}
      <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Toggle
          checked={tracking}
          onChange={setTracking}
          testId={BEHAVIOR_SIGNALS.trackingToggle}
          label="Tracking Supported"
          help="The system expects and can reliably collect this signal from supported versions."
        />
        <Toggle
          checked={enabled}
          onChange={setEnabled}
          testId={BEHAVIOR_SIGNALS.enabledToggle}
          label="Enabled for Scoring"
          help="A versioned scoring model is allowed to use this signal. Enabling alone does not modify the current CPS V2 equation — Product Behaviour must be added by a future versioned model."
        />
      </div>

      {tracking && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Min Android App Version
            </label>
            <input
              data-testid={BEHAVIOR_SIGNALS.minAndroidInput}
              value={minA}
              onChange={(e) => setMinA(e.target.value)}
              placeholder="e.g. 5.2.0"
              className="mt-1 w-full text-sm border border-slate-300 rounded px-2 py-1 font-mono"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Min iOS App Version
            </label>
            <input
              data-testid={BEHAVIOR_SIGNALS.miniOsInput}
              value={minI}
              onChange={(e) => setMinI(e.target.value)}
              placeholder="e.g. 4.8.0"
              className="mt-1 w-full text-sm border border-slate-300 rounded px-2 py-1 font-mono"
            />
          </div>
        </div>
      )}

      {dirty && (
        <div className="mt-3 flex items-center justify-end">
          <button
            data-testid={BEHAVIOR_SIGNALS.saveBtn}
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-60"
          >
            <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}


export default function BehaviorSignals() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/behavior-signals/status");
      setStatus(data);
    } catch (e) {
      toast.error("Failed to load signal status");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading && !status) return <div className="p-10 text-sm text-slate-500">Loading behavioural signals…</div>;
  if (!status) return null;

  const cats = Array.from(new Set(status.signals.map(s => s.category)));
  const visible = status.signals.filter(s => category === "all" || s.category === category);

  return (
    <div
      data-testid={BEHAVIOR_SIGNALS.root}
      className="max-w-6xl mx-auto p-6 space-y-4"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-indigo-600" />
            <h1 className="font-heading text-xl font-bold text-slate-900">
              Behavioural Signals
            </h1>
            <HintIcon
              hint={
                <>
                  Behavioural signals are product-usage events emitted by the PrepHacker mobile app or the web subscription page (e.g. <code>mock_test_started</code>, <code>PAGE_OPENED</code>, <code>PAY_NOW_CLICKED</code>).<br /><br />
                  <b>Mobile app signals:</b> collection is separate from scoring — enabling them here just tells Connect to store them. The current CPS V2 formula does <b>NOT</b> use them yet; that requires a future versioned model.<br /><br />
                  <b>Web subscription page signals (v29.13):</b> are <b>already scored</b>. The strongest event in the last 14 days contributes a bounded ±15 boost to both the legacy hybrid score and CPS V2 (terminal events like <code>PAYMENT_SUCCESS</code> are excluded).
                </>
              }
            />
          </div>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            Configure and monitor product-behaviour signals sent by the PrepHacker app and the web subscription page. Mobile-app signal collection is separate from scoring — enabling a mobile signal does <b>not</b> modify CPS. Web subscription page signals (v29.13) <b>do</b> contribute a bounded ±15 boost to CPS out of the box.
          </p>
        </div>
        <button
          data-testid={BEHAVIOR_SIGNALS.refreshBtn}
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-indigo-700 disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        <button
          onClick={() => setCategory("all")}
          className={`px-2 py-1 rounded-full border ${category === "all" ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-indigo-400"}`}
        >All · {status.signals.length}</button>
        {cats.map(c => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={`px-2 py-1 rounded-full border ${category === c ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-indigo-400"}`}
          >{c} · {status.signals.filter(s => s.category === c).length}</button>
        ))}
      </div>

      <div className="space-y-3">
        {visible.map(sig => (
          <SignalCard key={sig.key} sig={sig} onUpdate={load} />
        ))}
      </div>

      <div className="text-[11px] text-slate-500 pt-3">
        Generated: <span className="font-mono">{status.generated_at}</span>
      </div>
    </div>
  );
}
